import type { IconName } from '../AppIcon';

// Keyed by NotificationType.Name exactly as seeded in BluHRMSContext.cs.
// A missing key renders the generic 'Info' glyph, so every seeded type is listed.
export const notificationIconMap: Record<string, IconName> = {
  ClockInReminder: 'Clock',
  ClockOutReminder: 'LogOut',
  TimeSheetsCreated: 'CalendarClock',
  OvertimeVerification: 'Clock',
  BreakInNotification: 'Coffee',
  BreakOutNotification: 'Briefcase',
  LeaveRequest: 'Palmtree',
  LeaveRequestResponse: 'Palmtree',
  HolidayNotification: 'CalendarDays',
  OvertimeApprovalRequest: 'Clock',
  OvertimeApprovalResponse: 'Clock',
  // Hourly pay (docs/hourly-pay-spec.md §9).
  ShiftPayApprovalDigest: 'ListChecks',
  ShiftPayDecided: 'Clock4',
  UsersMissingClockInNotification: 'AlarmClock',
  EarlyClockOutNotification: 'LogOut',
  StillInBreakVerification: 'Coffee',
  LeaveRequestUndoResponse: 'Palmtree',
  PayslipGeneratedNotification: 'Receipt',
  AssetOtpVerification: 'KeyRound',
  AttendanceCorrectionRequestSubmitted: 'ClipboardCheck',
  AttendanceCorrectionRequestDecision: 'ClipboardCheck',
  ScheduleChanged: 'CalendarClock',
  OnboardingStarted: 'UserCheck',
  OnboardingStartDateReminder: 'UserCheck',
  OffboardingStarted: 'UserMinus',
  OffboardingLastDayReminder: 'UserMinus',
  InternalMovementInitiated: 'ArrowRightLeft',
  InternalMovementEffectiveDateReminder: 'ArrowRightLeft',

  // Approval-wave types (57-97). The leave family reuses the existing Palmtree so
  // the bell reads as one story rather than a new icon per notification.
  LeaveCancellationRequest: 'Palmtree',
  LeaveCancellationDecision: 'Palmtree',
  LeaveApprovalStepPending: 'Palmtree',
  LeaveApprovalStepProgress: 'Palmtree',
  LeaveApprovalEscalated: 'Palmtree',
  LeaveAutoApproved: 'Palmtree',
  RecurringLeaveSubmitted: 'Palmtree',
  RecurringLeaveCancelled: 'Palmtree',
  ReturnToWorkRequired: 'ClipboardCheck',
  ReturnToWorkCompleted: 'ClipboardCheck',
  ReturnToWorkDocumentsUploaded: 'ClipboardCheck',
  ShortLeaveRequest: 'Timer',
  ShortLeaveDecision: 'Timer',
  ShortLeaveWithdrawn: 'Timer',
  EncashmentRequest: 'Banknote',
  EncashmentDecision: 'Banknote',
  ClaimSubmitted: 'Receipt',
  ClaimDecision: 'Receipt',
  ClaimPaid: 'Receipt',
  AssetRequestSubmitted: 'Package',
  AssetRequestDecision: 'Package',
  AssetRequestFulfilled: 'Package',
  InternalMovementPendingHr: 'ArrowRightLeft',
  InternalMovementPendingManager: 'ArrowRightLeft',
  InternalMovementDecision: 'ArrowRightLeft',
  EmployeeMovementSubmitted: 'ArrowRightLeft',
  EmployeeMovementDecision: 'ArrowRightLeft',
  PublicProfileSubmitted: 'UserSquare',
  PublicProfileDecision: 'UserSquare',
  CompOffCreditUpdated: 'CalendarDays',
  DelegationUpdated: 'Users',
  TimesheetSubmitted: 'CalendarClock',
  TimesheetDecision: 'CalendarClock',
  GoalBlockerUpdated: 'OctagonAlert',
  PerformanceSnapshotSubmitted: 'BadgeCheck',
  PerformanceSnapshotAcknowledged: 'BadgeCheck',
  ReflectionManagerResponse: 'MessageSquareQuote',
  MentorAssignmentUpdated: 'Users',
  CheckInUpdated: 'CalendarClock',
  RestrictedHolidaySelectionWindow: 'CalendarHeart',
  GpssaContributionsPending: 'Landmark',

  // Pre-existing types that had no entry and were rendering the generic glyph.
  UserMonthlyPaySlip: 'Receipt',
  ShiftSwapRequested: 'ArrowRightLeft',
  ShiftSwapDecision: 'ArrowRightLeft',
  OutOfBoundsClock: 'MapPinOff',
  OutOfBoundsApprovalRequested: 'MapPinOff',
  OutOfBoundsApprovalDecision: 'MapPinOff',
  Tournament: 'Trophy',
  LeaveBlackout: 'CalendarDays',
  CarryForwardExpiring: 'CalendarDays',
  DocumentExpiryReminder: 'FileText',
  DocumentExpired: 'FileText',
  CchiExpiryReminder: 'FileText',
  DependentInsuranceUpdated: 'Users',
  BusinessRoleAssigned: 'UserCheck',
  BusinessRoleRevoked: 'UserMinus',
  Q12Nudge: 'Smile',
  WorkspaceStaffingRequired: 'Users',
  WorkspaceMilestoneBilling: 'Receipt',
  WorkspaceMilestoneOverdue: 'AlarmClock',
  TaskAssigned: 'ClipboardCheck',
  TaskSlaBreached: 'TimerOff',
  ChecklistApprovalRequested: 'UserCheck',
  ChecklistItemRejected: 'XCircle',
  TaskCommentAdded: 'MessageSquare',
  TaskDueToday: 'CalendarClock',
  TaskMentioned: 'AtSign',
  OnDemandNotification: 'Bell',
  SubscriptionTrialEndingSoon: 'CreditCard',
  SubscriptionTrialExpired: 'CreditCard',
  SubscriptionPaidRenewalDue: 'CreditCard',
  SubscriptionPaidOverdue: 'CreditCard',
  SubscriptionUpgradeRequest: 'CreditCard',
  SubscriptionHrUpgradeNudge: 'CreditCard',
  // Platform, Operator-only (CO-846 mail queue digest).
  MailQueueAlert: 'Mail',
  KsaMudadDeadlineReminder: 'Landmark',
  KsaPayrollRunReminder: 'Landmark',

  // Engagement Drivers types (112-115). Each reuses the glyph of its family so
  // the bell keeps reading as one story.
  AssetReplenishment: 'Package',
  RecognitionGapReminder: 'Smile',
  CheckInReminder: 'CalendarClock',
  CheckInReminderDigest: 'CalendarClock',
};
